import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Boxes, ChevronRight, Loader2, CalendarRange } from "lucide-react";
import { vendorSetStock } from "@/lib/vendorApi";

const dot = (show, id) => show && <span className="me-1.5 inline-block w-2 h-2 rounded-full bg-rose-600 align-middle" data-testid={id} />;

export function RestockRow({ item, unread, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const days = Math.max(3, Math.floor((Date.now() - new Date(item.unavailable_since).getTime()) / 86400000));
  const undo = async (res) => {
    try {
      await vendorSetStock(item.product_id, { in_stock: false, if_updated_at: res.updated_at, restore_unavailable_since: res.prev_unavailable_since || undefined });
      toast.success(`${item.title} — wapas Unavailable`);
      setDone(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo nahi ho paya");
    }
  };
  const restock = async () => {
    setBusy(true);
    try {
      const res = await vendorSetStock(item.product_id, { in_stock: true });
      setDone(true);
      toast.success(`${item.title} — Available`, { duration: 5000, action: { label: "Undo", onClick: () => undo(res) } });
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update nahi ho paya");
    } finally { setBusy(false); }
  };
  if (done) return null;
  return (
    <div className="flex items-start gap-3 bg-white border border-amber-200 rounded-xl p-3" data-testid={`restock-alert-${item.product_id}`}>
      <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-amber-700 bg-amber-50"><Boxes className="w-4 h-4" /></span>
      <Link to={item.link} className="flex-1 min-w-0 group" data-testid={`restock-alert-link-${item.product_id}`}>
        <div className="text-sm font-semibold text-slate-900 line-clamp-2 group-hover:text-emerald-800">
          {dot(unread, `restock-alert-unread-${item.product_id}`)}"{item.title}" {days} din se Unavailable hai
        </div>
        <span className="inline-flex items-center gap-0.5 mt-1 text-xs font-semibold text-slate-500">Quick Stock kholein <ChevronRight className="w-3 h-3" /></span>
      </Link>
      <button onClick={restock} disabled={busy} className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400" data-testid={`restock-available-btn-${item.product_id}`}>
        {busy && <Loader2 className="w-3 h-3 animate-spin" />} Available karein
      </button>
    </div>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 truncate">{label}</div>
    <div className={`text-sm font-bold ${value == null ? "text-slate-400" : "text-slate-900"}`} data-testid={testid}>{value == null ? "Record nahi hota" : Number(value).toLocaleString("en-IN")}</div>
  </div>
);

const d = (s) => new Date(`${s}T00:00:00+05:30`).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" });

export function WeeklySummaryRow({ item, unread }) {
  const s = item.shop || {}, a = item.ads || {};
  return (
    <Link to={item.link} className="block bg-white border border-emerald-200 hover:border-emerald-400 rounded-xl p-3 transition-colors" data-testid={`weekly-summary-${item.period_start}`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <CalendarRange className="w-4 h-4 text-emerald-700 flex-shrink-0" />
        {dot(unread, "weekly-summary-unread")}Weekly summary · {d(item.period_start)} – {d(item.period_end)} (Mon–Sun, IST)
      </div>
      <div className="mt-2 text-[11px] font-bold text-emerald-800">Shop Performance</div>
      <div className="grid grid-cols-3 gap-2 mt-1" data-testid="weekly-shop-stats">
        <Stat label="Shop views" value={s.shop_views} testid="weekly-shop-views" />
        <Stat label="Product views" value={s.product_views} testid="weekly-product-views" />
        <Stat label="Service views" value={s.service_views} testid="weekly-service-views" />
        <Stat label="Product WhatsApp orders" value={s.whatsapp_clicks} testid="weekly-whatsapp-clicks" />
        <Stat label="Shop WhatsApp clicks" value={s.shop_whatsapp_clicks} testid="weekly-shop-whatsapp-clicks" />
        <Stat label="Call clicks" value={s.call_clicks} testid="weekly-call-clicks" />
        <Stat label="Unavailable now" value={s.products_unavailable} testid="weekly-unavailable" />
      </div>
      {s.contact_tracking_since && <div className="text-[10px] text-amber-700 mt-1" data-testid="weekly-tracking-since">Call / Shop WhatsApp ginti {new Date(s.contact_tracking_since).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })} se shuru hui (adhoora hafta).</div>}
      <div className="mt-2 text-[11px] font-bold text-emerald-800">Ads Performance</div>
      {item.has_ads ? (
        <div className="grid grid-cols-3 gap-2 mt-1" data-testid="weekly-ad-stats">
          <Stat label="Impressions" value={a.impressions} testid="weekly-ad-impressions" />
          <Stat label="Views" value={a.views} testid="weekly-ad-views" />
          <Stat label="Clicks" value={a.clicks} testid="weekly-ad-clicks" />
        </div>
      ) : <div className="text-xs text-slate-500 mt-1" data-testid="weekly-no-ads">Abhi koi ad nahi hai.</div>}
      <div className="text-[10px] text-slate-400 mt-2">Call aur Shop WhatsApp = shop page ke buttons (har customer 30 min me ek baar). "Unavailable now" = summary banne ke samay.</div>
    </Link>
  );
}

export function AdExpiryRow({ item, unread }) {
  const end = new Date(item.ends_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
  return (
    <div className="flex items-start gap-3 bg-white border border-amber-200 rounded-xl p-3" data-testid={`ad-expiry-alert-${item.ad_id}`}>
      <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-amber-700 bg-amber-50"><CalendarRange className="w-4 h-4" /></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 line-clamp-2">
          {dot(unread, `ad-expiry-alert-unread-${item.ad_id}`)}Aapka free ad "{item.title || item.ad_id}" 24 ghante mein khatam ho raha hai
        </div>
        <div className="text-[11px] text-slate-500 line-clamp-1" data-testid={`ad-expiry-alert-ends-${item.ad_id}`}>Khatam: {end} IST · PIN {(item.pincodes || []).join(", ")}</div>
      </div>
      <Link to={item.link} className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-3 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400" data-testid={`ad-expiry-renew-btn-${item.ad_id}`}>
        Renew <ChevronRight className="w-3 h-3" />
      </Link>
    </div>
  );
}
